import { createAuthClient, resolveSafeReturnTo, type AuthClient } from '@sigfarm/auth-client-vue';

/**
 * Sessão humana do webapp (WA01), sobre `@sigfarm/auth-client-vue`.
 *
 * Reaproveita do Landwatch só o núcleo: cliente auth, portal, callback/troca,
 * aquisição single-flight (do SDK), `authFetch` Bearer com UM refresh após
 * 401 (do SDK) e logout. Token só em memória; nada de segredo em storage.
 *
 * Identidade vem da plataforma central; QUEM pode ver o quê vem da API RFID
 * (`/v1/access/me`). Nenhuma decisão de autorização é tomada aqui.
 */

export type AccessState = 'pending' | 'active' | 'suspended' | 'conflict';

export interface AccessMe {
  readonly status: AccessState;
  readonly isPlatformAdmin: boolean;
  readonly grants: readonly { tenantId: string; allFarms: boolean; farmIds: readonly string[] }[];
}

export interface WebAuthOptions {
  readonly authApiBaseUrl: string;
  readonly authPortalBaseUrl: string;
  readonly appBaseUrl: string;
  /** Somente `vite dev` em loopback: a API decide (bypass próprio, também só loopback). */
  readonly devBypass?: boolean;
  readonly fetch?: typeof fetch;
  readonly storage?: Pick<Storage, 'length' | 'key' | 'removeItem'>;
  readonly navigate?: (url: string) => void;
}

export interface WebAuth {
  authFetch(input: string, init?: RequestInit): Promise<Response>;
  /** URL do portal central com retorno ao callback deste app. */
  loginUrl(returnTo?: unknown): string;
  /** Troca a sessão central por token (callback) e devolve o caminho interno seguro. */
  completeCallback(returnTo: unknown): Promise<string>;
  /** Contexto RFID do usuário; uma chamada em voo por vez, guardado até `forgetAccess`. */
  access(): Promise<AccessMe | null>;
  forgetAccess(): void;
  logout(): Promise<void>;
  safeReturnPath(raw: unknown): string;
}

/**
 * URL da API RFID para um caminho `/v1/...` (WA01-R1). Sem
 * `VITE_RFID_API_BASE_URL`: mesma origem (proxy do Vite ou reverse proxy).
 * Com ela: base absoluta http(s) normalizada, sem barra duplicada. Nunca usada
 * para portal/callback/auth central.
 */
export function apiUrl(path: string): string {
  const raw = import.meta.env.VITE_RFID_API_BASE_URL?.trim() ?? '';
  if (raw === '') return path;
  if (raw !== normalizedFrom) {
    const url = new URL(raw);
    const localHttp = import.meta.env.DEV && url.protocol === 'http:' && isLoopbackHostname(url.hostname);
    const originOnly = /^\/+$/u.test(url.pathname) && url.search === '' && url.hash === '';
    if (
      !(url.protocol === 'https:' || localHttp) ||
      url.username !== '' ||
      url.password !== '' ||
      !originOnly
    ) {
      throw new Error(
        'VITE_RFID_API_BASE_URL exige origem HTTPS exata (HTTP apenas em loopback durante desenvolvimento).',
      );
    }
    normalizedBase = url.origin;
    normalizedFrom = raw;
  }
  return `${normalizedBase}${path.startsWith('/') ? path : `/${path}`}`;
}
let normalizedFrom = '';
let normalizedBase = '';

/** Prefixo das preferências locais (cliente/fazenda); apagadas no logout. */
export const LOCAL_PREFIX = 'rfid.';
export const DEFAULT_PATH = '/cliente';
const AUTH_PATHS = new Set(['/login', '/auth/callback']);

export function createWebAuth(options: WebAuthOptions): WebAuth {
  const fetchImpl = options.fetch ?? ((input, init) => fetch(input, init));
  const appOrigin = new URL(options.appBaseUrl).origin;
  const client: AuthClient = createAuthClient({
    authApiBaseUrl: options.authApiBaseUrl,
    authPortalBaseUrl: options.authPortalBaseUrl,
    appBaseUrl: appOrigin,
    allowedReturnOrigins: [appOrigin],
    defaultReturnTo: `${appOrigin}${DEFAULT_PATH}`,
    fetch: fetchImpl,
  });
  let accessInFlight: Promise<AccessMe | null> | null = null;
  let accessValue: AccessMe | null = null;

  /**
   * Só caminho INTERNO. O SDK sanitiza; a origem é conferida de novo porque
   * `//outro.host` começa com `/` e resolveria para fora.
   */
  function safeReturnPath(raw: unknown): string {
    if (typeof raw !== 'string' || raw.includes('\\')) return DEFAULT_PATH;
    const resolved = resolveSafeReturnTo({
      returnTo: raw,
      appBaseUrl: appOrigin,
      authPortalBaseUrl: options.authPortalBaseUrl,
      defaultReturnTo: `${appOrigin}${DEFAULT_PATH}`,
      allowedOrigins: [appOrigin],
    });
    const url = new URL(resolved);
    if (url.origin !== appOrigin || AUTH_PATHS.has(url.pathname)) return DEFAULT_PATH;
    return `${url.pathname}${url.search}${url.hash}`;
  }

  const authFetch = (input: string, init: RequestInit = {}) =>
    options.devBypass === true ? fetchImpl(input, init) : client.authFetch(input, init);

  async function loadAccess(): Promise<AccessMe | null> {
    const response = await authFetch(apiUrl('/v1/access/me'), { headers: { accept: 'application/json' } });
    if (response.status === 401) return null;
    if (response.status === 409) return { status: 'conflict', isPlatformAdmin: false, grants: [] };
    if (response.status === 403) return { status: 'suspended', isPlatformAdmin: false, grants: [] };
    if (!response.ok) throw new Error(`Falha ao consultar acesso (HTTP ${response.status}).`);
    return (await response.json()) as AccessMe;
  }

  return {
    authFetch,
    safeReturnPath,
    loginUrl(returnTo) {
      const callback = new URL('/auth/callback', appOrigin);
      callback.searchParams.set('returnTo', safeReturnPath(returnTo));
      const login = new URL('/login', options.authPortalBaseUrl);
      login.searchParams.set('returnTo', callback.toString());
      return login.toString();
    },
    async completeCallback(returnTo) {
      if (options.devBypass !== true) await client.exchangeSession();
      return safeReturnPath(returnTo);
    },
    access() {
      if (accessValue !== null) return Promise.resolve(accessValue);
      accessInFlight ??= loadAccess()
        .then((value) => (accessValue = value))
        .finally(() => (accessInFlight = null));
      return accessInFlight;
    },
    forgetAccess() {
      accessValue = null;
    },
    async logout() {
      // Revogação central é melhor esforço; o estado local some SEMPRE, antes do redirect.
      if (options.devBypass !== true) {
        await Promise.allSettled([
          client.logout(),
          fetchImpl(new URL('/api/auth/sign-out', options.authApiBaseUrl).toString(), {
            method: 'POST',
            credentials: 'include',
            headers: { 'content-type': 'application/json' },
            body: '{}',
          }),
        ]);
      }
      client.clearSession();
      accessValue = null;
      accessInFlight = null;
      const storage = options.storage ?? safeLocalStorage();
      if (storage !== null) {
        const keys: string[] = [];
        for (let index = 0; index < storage.length; index += 1) {
          const key = storage.key(index);
          if (key !== null && key.startsWith(LOCAL_PREFIX)) keys.push(key);
        }
        for (const key of keys) storage.removeItem(key);
      }
      (options.navigate ?? navigator)('/login');
    },
  };
}

function safeLocalStorage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

/* ------------------------------------------------------------------ *
 * Instância do app
 * ------------------------------------------------------------------ */

let current: WebAuth | null = null;
let navigator: (url: string) => void = (url) => window.location.assign(url);

/** Troca a navegação de página inteira (testes). */
export function useNavigator(navigate: (url: string) => void): void {
  navigator = navigate;
}

/**
 * Instância única. Sem `VITE_SIGFARM_AUTH_*` não há fallback para outro
 * ambiente: staging e produção precisam declarar o próprio portal.
 */
export function webAuth(): WebAuth {
  if (current !== null) return current;
  const env = import.meta.env;
  const appBaseUrl = env.VITE_SIGFARM_APP_BASE_URL ?? window.location.origin;
  const loopback = isLoopbackHostname(window.location.hostname);
  const devBypass = env.DEV && env.VITE_RFID_AUTH_DEV_BYPASS === '1' && loopback;
  const authApiBaseUrl = env.VITE_SIGFARM_AUTH_API_BASE_URL;
  const authPortalBaseUrl = env.VITE_SIGFARM_AUTH_PORTAL_BASE_URL;
  if (!devBypass && (authApiBaseUrl === undefined || authPortalBaseUrl === undefined)) {
    throw new Error('VITE_SIGFARM_AUTH_API_BASE_URL e VITE_SIGFARM_AUTH_PORTAL_BASE_URL são obrigatórias.');
  }
  current = createWebAuth({
    authApiBaseUrl: authApiBaseUrl ?? appBaseUrl,
    authPortalBaseUrl: authPortalBaseUrl ?? appBaseUrl,
    appBaseUrl,
    devBypass,
  });
  return current;
}

function isLoopbackHostname(hostname: string): boolean {
  return ['localhost', '127.0.0.1', '::1', '[::1]'].includes(hostname.trim().toLowerCase());
}

/** Troca a instância (testes). */
export function useWebAuth(instance: WebAuth | null): void {
  current = instance;
}

/**
 * Resposta humana negada: 401 volta ao login; pendente/suspenso vão para a
 * tela de estado; admin exigido volta para o cliente. Devolve `true` quando
 * redirecionou (o chamador não deve renderizar dado).
 */
export async function redirectOnDenied(response: Response): Promise<boolean> {
  if (response.status !== 401 && response.status !== 403) return false;
  webAuth().forgetAccess();
  if (response.status === 401) {
    navigator(`/login?returnTo=${encodeURIComponent(currentPath())}`);
    return true;
  }
  const body = (await response.clone().json().catch(() => ({}))) as { errorCode?: string };
  const target = DENIED_TARGET[body.errorCode ?? ''];
  if (target === undefined) return false;
  navigator(target);
  return true;
}

/** Só as recusas de ACESSO redirecionam; outra recusa 403 fica na tela que pediu. */
const DENIED_TARGET: Record<string, string> = {
  RFID_ACCESS_PENDING: '/pendente',
  RFID_ACCESS_SUSPENDED: '/suspenso',
  CENTRAL_ACCOUNT_INACTIVE: '/suspenso',
  PLATFORM_ADMIN_REQUIRED: DEFAULT_PATH,
};

function currentPath(): string {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}
